<script setup lang="ts">
import { computed } from "vue";
import { delay } from "lodash";
import NoteMenuState from "@/stores/NoteMenuState";
import CanvasSettingsState from "@/stores/CanvasSettingsState";
import type { Legend } from "@/types/legend.types";
import { Logger } from "@/logger";
import { trackFeature } from "@/analytics/drawing-analytics";
import ScaleMenuState from "@/stores/ScaleMenuState";
import ScaleSettings from "@/components/canvas/header/ScaleSettings.vue";
import { useHeaderPopoverLock } from "@/composable/useHeaderPopoverLock";

// Légende active + filtre des catalogues affichés en onglets. L'échelle a son
// propre bouton dans l'en-tête (header/ScaleSelector.vue), sauf quand la place
// manque : elle rejoint alors cette popup (withScale).
withDefaults(defineProps<{ withScale?: boolean }>(), { withScale: false });

const popoverLock = useHeaderPopoverLock();

function persistActiveCategories() {
  trackFeature("legend_catalog_filtered");
  delay(() => NoteMenuState.persistActiveCategories(), 200);
}

function activateLegend(legend: Legend) {
  // Don't change categories if same legend is selected
  if (CanvasSettingsState.activeLegend?.id === legend.id) {
    return;
  }

  Logger.info(`LegendSelector : activating legend ${legend.name}`);
  CanvasSettingsState.onLegendChange(legend);

  // legend.categories est déjà la liste ordonnée de référence : le premier catalogue
  // sélectionné par défaut est simplement le premier de cette liste.
  const firstCategory = CanvasSettingsState.activeLegend?.categories?.[0];
  if (firstCategory) {
    NoteMenuState.activeCategories = [firstCategory.name];
    NoteMenuState.persistActiveCategories();
  }
}

function onLegendSelect(id: number) {
  const legend = legends.value.find((l) => l.id === id);
  if (legend) activateLegend(legend);
}

// legend.categories est la liste ordonnée de référence : on ne garde que celles
// utilisées par au moins une note (pour ne pas afficher un filtre vide), dans cet ordre.
// Toute note appartient obligatoirement à au moins un catalogue — pas de groupe "sans
// catalogue" à gérer ici.
const categories = computed(() => {
  const categories: Map<string, boolean> = new Map<string, boolean>();
  const legendCategories = CanvasSettingsState.activeLegend?.categories ?? [];
  const notes = NoteMenuState.noteConfigs.filter((n) => n.type !== "photo");

  for (const category of legendCategories) {
    if (notes.some((n) => category.noteIds.includes(n.id))) {
      categories.set(category.name, true);
    }
  }
  return categories;
});

const legends = computed(() => CanvasSettingsState.legends);
</script>

<template>
  <el-popover
    placement="bottom-start"
    :width="260"
    trigger="click"
    effect="dark"
    :show-arrow="false"
    popper-class="legend-settings-popover"
    @show="popoverLock.onShow"
    @hide="popoverLock.onHide"
  >
    <template #reference>
      <slot />
    </template>

    <!-- Legend select section -->
    <div class="legend-popover-section">
      <div class="text-white-50 mb-1">
        <small>{{ $t("canvas:settings.key.legend") }}</small>
      </div>
      <!-- Liste non téléportée : sinon choisir une légende compte comme un
           clic hors de la popup, qui se refermerait. -->
      <el-select
        :model-value="CanvasSettingsState.activeLegend?.id"
        class="legend-select w-100"
        popper-class="legend-select-dropdown"
        size="large"
        :teleported="false"
        @change="onLegendSelect"
      >
        <el-option
          v-for="legend in legends"
          :key="legend.id"
          :label="legend.name"
          :value="legend.id"
        />
      </el-select>
    </div>

    <!-- Scale section (petits écrans : un seul bouton pour les deux) -->
    <div
      v-if="withScale && ScaleMenuState.scales.length"
      class="legend-popover-section mt-2 pt-2"
    >
      <ScaleSettings />
    </div>

    <!-- Categories section -->
    <div v-if="categories.size" class="legend-popover-section mt-2 pt-2">
      <div class="text-white-50 mb-1">
        <small>{{ $t("canvas:draggable.catalogues") }}</small>
      </div>
      <el-checkbox-group
        class="w-100 d-flex flex-column"
        v-model="NoteMenuState.activeCategories"
        @click.stop
      >
        <el-checkbox
          class="w-100 legend-checkbox"
          v-for="(category, index) in categories"
          :key="index"
          :value="category[0]"
          @click="persistActiveCategories"
          size="small"
        >
          <span class="text-white">{{ category[0] }}</span>
        </el-checkbox>
      </el-checkbox-group>
    </div>
  </el-popover>
</template>

<style lang="scss" scoped>
.legend-popover-section + .legend-popover-section {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}

.legend-checkbox {
  margin-right: 0 !important;
}
</style>
